import { Box, Link, Typography } from "@mui/material";
import { DataGridPremium, DataGridPremiumProps, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";
import InlineLoader from "../../../../../components/inlineLoader/InlineLoader.tsx";
import ScrollableFlexContainer from "../../../../../components/ScrollableFlexContainer.tsx";
import {
  ConditionField,
  DimensionDictionary,
  EMPTY_CROSSFILTERED_CONDITION_VALUES,
} from "../../../../api/biClient.types.ts";
import { isCrossFilterToBeRefreshed } from "./conditionsHelper.tsx";
import { getNewSelection } from "./getNewSelection.ts";

interface Props extends DataGridPremiumProps {
  field: ConditionField;
  values: DimensionDictionary[];
  selectedValues: unknown[];
  readonly?: boolean;
  onChanged: (selectedValues: string[]) => void;
}

export default function TextFilterGrid(props: Props) {
  const { field, values, selectedValues, readonly, onChanged } = props;
  const isLoading = isCrossFilterToBeRefreshed(field);
  const [rowsSelection, setRowsSelection] = useState<GridRowSelectionModel>(() => {
    return values
      .filter((v) => selectedValues.includes(v[field.meta.keyFieldName]))
      .map((row): number => row["id"] as number)
      .filter((v): v is number => v !== undefined);
  });

  const onSelectionChanged = useCallback(
    (rowNumbers: GridRowSelectionModel) => {
      const newSelection = getNewSelection(rowNumbers as number[], values, field.meta.keyFieldName);
      onChanged(newSelection);
      setRowsSelection(rowNumbers);
    },
    [values, field.meta.keyFieldName, onChanged]
  );

  return (
    <ScrollableFlexContainer sx={{ minHeight: "255px" }}>
      <DataGridPremium<DimensionDictionary>
        rows={values}
        sx={(theme) => ({
          "--DataGrid-rowBorderColor": theme.palette.divider,
          border: "1px solid " + theme.palette.divider,
          ".MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          ".MuiDataGrid-cell:focus-within": {
            outline: "unset",
          },
          ".MuiDataGrid-columnHeader:focus-within": {
            outline: "unset",
          },
          ".MuiDataGrid-virtualScroller": {
            pointerEvents: isLoading ? "none" : "inherit",
          },
          ".MuiDataGrid-row": {
            ".MuiDataGrid-cell": {
              borderTop: "none",
              ".MuiDataGrid-groupingCriteriaCell": {
                fontWeight: "500",
                ".MuiDataGrid-groupingCriteriaCellToggle": {
                  marginRight: "4px",
                },
              },
              "&.MuiDataGrid-cellCheckbox": {
                // width: "40px",
                // justifyContent: "end",
              },
            },
          },
          ".MuiButtonBase-root.MuiCheckbox-root": {
            p: 0.75,
            // width: "auto",
          },
          ".MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--borderTop": {
            border: "none",
          },
        })}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: field.meta.keyFieldName,
                sort: "asc",
              },
            ],
          },
        }}
        rowHeight={24}
        checkboxSelection={!readonly}
        rowSelectionModel={rowsSelection}
        disableRowSelectionOnClick={readonly}
        onRowSelectionModelChange={onSelectionChanged}
        rowSelectionPropagation={{ descendants: true, parents: true }}
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        hideFooter
        disableColumnMenu
        columnHeaderHeight={30}
        slots={{
          loadingOverlay: InlineLoader,
          noRowsOverlay: () => <NoRows field={field} />,
        }}
        loading={isLoading}
        {...props}
      />
    </ScrollableFlexContainer>
  );
}

const isGroupExpandedByDefault = () => true;

function NoRows({ field }: { field: ConditionField }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        flexDirection: "column",
        textAlign: "center",
      }}
    >
      {field.crossFilter && field.crossFilter.values === EMPTY_CROSSFILTERED_CONDITION_VALUES ? (
        [
          <Typography key="title">There is no data available.</Typography>,
          <Typography key="body" color="textSecondary" px={1}>
            Please review the cross-filter configuration or refer to{" "}
            <Link
              href="https://help.entrilia.com/en/articles/8570469-cross-filtering-in-reports"
              target="_blank"
              rel="noreferrer"
            >
              the documentation for more information.
            </Link>
          </Typography>,
        ]
      ) : (
        <Typography key="title">No Rows</Typography>
      )}
    </Box>
  );
}
